import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";
import Button from "../../../components/ui/Button";
import { sendEmail } from "../../../utils/emailService"; // ✅ EmailJS helper

const ContactSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [contactForm, setContactForm] = useState({
    from_name: "",
    from_email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const contactInfo = [
    {
      icon: "MapPin",
      title: "Visit Our Campus",
      details: ["Tambaram, Chennai", "Tamil Nadu 600045, India"]
    },
    {
      icon: "Phone",
      title: "Call Us",
      details: ["+91 9025608199"]
    },
    {
      icon: "Mail",
      title: "Email Us",
      details: ["nipixtech@nipixtechnology.com", "admin@nipixtechnology.com"]
    },
    {
      icon: "Clock",
      title: "Office Hours",
      details: ["Mon - Fri: 9:00 AM - 6:00 PM", "Sat: 10:00 AM - 4:00 PM"]
    }
  ];

  const socialLinks = [
    { icon: "Linkedin", name: "LinkedIn", url: "https://www.linkedin.com/company/96950353", color: "text-blue-600" },
    { icon: "Instagram", name: "Instagram", url: "https://www.instagram.com/nipixtechnology?igsh=NjM4ZXAyZ3piN21v", color: "text-pink-500" }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    const element = document.getElementById("contact");
    if (element) observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleInputChange = (field, value) => {
    setContactForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const templateParams = {
      form_type: "contact",
      from_name: contactForm.from_name,
      from_email: contactForm.from_email,
      subject: contactForm.subject,
      message: contactForm.message,
      timestamp: new Date().toISOString()
    };

    try {
      await sendEmail(templateParams);
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setContactForm({
        from_name: "",
        from_email: "",
        subject: "",
        message: ""
      });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (err) {
      console.error("❌ Contact form error:", err);
      setIsSubmitting(false);
      alert("There was an error submitting your message. Please try again.");
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-background to-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            <span className="text-nipix-blue">G</span>
            <span className="text-text-primary">et </span>
            <span className="text-nipix-blue">I</span>
            <span className="text-text-primary">n </span>
            <span className="text-nipix-blue">T</span>
            <span className="text-text-primary">ouch</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Have questions about our courses or internship programs? We're here to help you start your career transformation journey.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Contact Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-border hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={info.icon} size={20} className="text-primary" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-text-primary mb-2">{info.title}</h3>
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-sm text-text-secondary mb-1">{detail}</p>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Social Media */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-border"
            >
              <h3 className="font-semibold text-text-primary mb-4 flex items-center">
                <Icon name="Share2" size={20} className="mr-2" strokeWidth={2} />
                Follow Us
              </h3>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className={`w-10 h-10 rounded-full bg-surface flex items-center justify-center hover:bg-muted transition-colors duration-300 ${social.color}`}
                    title={social.name}
                  >
                    <Icon name={social.icon} size={18} strokeWidth={2} />
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form & Map */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-2 space-y-8"
          >
            {/* Contact Form or Success Message */}
            {submitSuccess ? (
              <div className="bg-white rounded-3xl shadow-2xl border border-success/40 p-12 text-center">
                <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="CheckCircle" size={40} className="text-success" strokeWidth={2} />
                </div>
                <h3 className="text-2xl font-bold text-success mb-4">Message Sent Successfully!</h3>
                <p className="text-lg text-text-secondary mb-4">
                  Thank you for reaching out to us. Our team will get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl shadow-2xl border border-border overflow-hidden">
                <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
                  <h3 className="text-xl font-bold flex items-center">
                    <Icon name="MessageSquare" size={24} className="mr-3" strokeWidth={2} />
                    Send us a Message
                  </h3>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Input
                      label="Your Name"
                      type="text"
                      placeholder="Enter your full name"
                      value={contactForm.from_name}
                      onChange={(e) => handleInputChange("from_name", e.target.value)}
                      required
                    />
                    <Input
                      label="Email Address"
                      type="email"
                      placeholder="your.email@example.com"
                      value={contactForm.from_email}
                      onChange={(e) => handleInputChange("from_email", e.target.value)}
                      required
                    />
                  </div>

                  <Input
                    label="Subject"
                    type="text"
                    placeholder="What would you like to discuss?"
                    value={contactForm.subject}
                    onChange={(e) => handleInputChange("subject", e.target.value)}
                    required
                  />

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Message</label>
                    <textarea
                      value={contactForm.message}
                      onChange={(e) => handleInputChange("message", e.target.value)}
                      placeholder="Type your message here..."
                      rows={5}
                      required
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors duration-250 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    loading={isSubmitting}
                    iconName="Send"
                    iconPosition="right"
                  >
                    {isSubmitting ? "Sending Message..." : "Send Message"}
                  </Button>
                </form>
              </div>
            )}

            {/* Google Maps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden"
            >
              <div className="p-6 border-b border-border">
                <h3 className="font-semibold text-text-primary flex items-center">
                  <Icon name="MapPin" size={20} className="mr-2" strokeWidth={2} />
                  Visit Our Campus
                </h3>
              </div>
              <div className="h-80 relative">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="Nipix Technology Campus Location"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d408.97317607137325!2d80.10574013702544!3d12.924422116782617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTLCsDU1JzI3LjkiTiA4MMKwMDYnMjEuMyJF!5e1!3m2!1sen!2sin!4v1753964218097!5m2!1sen!2sin"
                  className="border-0"
                />
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg max-w-xs">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="Building" size={20} color="white" strokeWidth={2} />
                    </div>
                    <div>
                      <div className="font-semibold text-text-primary text-sm">Nipix Technology</div>
                      <div className="text-xs text-text-secondary">Tambaram , Chennai</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
            <Icon name="Headphones" size={48} className="mx-auto mb-4" strokeWidth={2} />
            <h3 className="text-2xl font-bold mb-4">Need Immediate Assistance?</h3>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Our career counselors are available to answer your questions and help you choose the right program for your goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+919025608199"
                className="bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300 inline-flex items-center justify-center space-x-2"
              >
                <Icon name="Phone" size={20} strokeWidth={2} />
                <span>Call Now: +91 9025608199</span>
              </a>
              <a
                href="https://wa.me/919025608199"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-primary transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <Icon name="MessageCircle" size={20} strokeWidth={2} />
                <span>WhatsApp Chat</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;
